import { useQuery } from "@apollo/client"
import { useParams, Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { useDocumentTitle } from "~/common/use-document-title"
import { lettermemePath, myLettermemeManagePath, settingsPath } from "~/common/paths"
import { GraphqlError } from "~/ui/errors"
import { ScreenLoading } from "~/ui/screen-loading"
import { Button } from "~/ui/button"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/ui/accordion"
import { CopyButton } from "~/ui/copy-button"

const lettermemeQuery = gql(/* GraphQL */ `
  query SetupLettermeme($id: ID!) {
    lettermeme(id: $id) {
      id
      name
      urlPath
      shareableEmailIn
      secretEmailIn
    }
  }
`)

export const LettermemeSetupScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>()

  const { data, error, loading } = useQuery(lettermemeQuery, {
    variables: { id: id! },
  })
  const lettermeme = data?.lettermeme

  useDocumentTitle(data?.lettermeme ? `Setup ${data.lettermeme.name}` : "Setup Lettermeme")

  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <h1 className="mb-4 text-2xl font-bold">Lettermeme not found</h1>
        <p>The lettermeme you're looking for doesn't exist.</p>
      </div>
    )
  }

  const customUrl = `lettermeme.com/${lettermeme.urlPath}`
  const shareableEmail = `${lettermeme.shareableEmailIn}@lettermeme.com`
  const secretEmail = `${lettermeme.secretEmailIn}@lettermeme.com`

  return (
    <div className="mx-auto mt-10 max-w-4xl p-8">
      <div className="mb-8">
        <Link to={myLettermemeManagePath({ id: lettermeme.id })} className="text-gray-400">
          ← back to lettermeme settings
        </Link>
        <h1 className="mb-2 mt-4 text-3xl font-bold">🎉 Congrats on creating your Lettermeme!</h1>
        <p className="text-lg text-gray-400">
          Your Lettermeme is now live at{" "}
          <Link
            to={lettermemePath({ urlPath: lettermeme.urlPath })}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white underline hover:text-white"
          >
            {customUrl}
          </Link>
        </p>
        <p className="mt-2 text-gray-400">
          Invite others to view or subscribe anytime — they'll get themed summaries as your
          Lettermeme grows.
        </p>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="mb-3 text-2xl font-bold">✉️ How it works</h2>
          <p className="text-gray-300">
            Your Lettermeme will start summarizing as soon as it begins receiving newsletters. Each
            issue you send in is automatically organized by theme and turned into an easy-to-read
            digest.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-bold">📬 Choose your setup method</h2>
          <Accordion
            type="single"
            collapsible
            className="w-full"
            defaultValue="direct-subscription"
          >
            <AccordionItem value="direct-subscription">
              <AccordionTrigger className="text-lg">
                Option 1: Direct Subscription with Secret Address
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <p className="text-gray-300">
                    Use your private subscription address to subscribe directly to newsletters, and
                    they'll be automatically forwarded back to you while being added to your
                    Lettermeme.
                  </p>

                  <div className="rounded-lg bg-gray-900 p-6">
                    <h4 className="mb-3 text-xl text-white">Your Secret Address:</h4>
                    <div className="mb-4 flex items-center justify-between rounded bg-gray-800 px-4 py-3">
                      <span className="font-mono text-lg text-white">{secretEmail}</span>
                      <CopyButton text={secretEmail} description="Secret email address" />
                    </div>
                    <div className="space-y-3 text-gray-300">
                      <p>
                        <span className="text-white">✓</span> Use this email when subscribing to
                        newsletters
                      </p>
                      <p>
                        <span className="text-white">✓</span> Newsletters will be forwarded to your
                        main email
                      </p>
                      <p>
                        <span className="text-white">✓</span> They'll also be added to your
                        Lettermeme automatically
                      </p>
                      <p className="pt-2 text-amber-400">
                        <span className="font-bold">⚠️ Important:</span> Do not share this address
                        with others. It's your private subscription address.
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gmail-forwarding">
              <AccordionTrigger className="text-lg">
                Option 2: Gmail Automatic Forwarding
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-6 pt-2">
                  <section>
                    <h3 className="mb-3 text-xl font-bold">
                      📬 Step 1: Set up automatic forwarding
                    </h3>
                    <p className="mb-4 text-gray-300">
                      Forward your favorite newsletters to your custom address:{" "}
                      <span className="rounded bg-gray-800 px-2 py-1 font-mono text-sm">
                        {shareableEmail}
                      </span>
                    </p>

                    <div className="rounded-lg bg-gray-900 p-6">
                      <h4 className="mb-3 text-xl text-white">In Gmail:</h4>
                      <ol className="list-decimal space-y-3 pl-6 text-gray-300">
                        <li>
                          Go to <span className="text-white">Settings ⚙</span> →{" "}
                          <span className="text-white">See all settings</span> →{" "}
                          <span className="text-white">Forwarding and POP/IMAP</span>
                        </li>
                        <li>
                          Click <span className="text-white">"Add a forwarding address"</span>
                        </li>
                        <li>
                          Enter{" "}
                          <span className="rounded bg-gray-800 px-2 py-1 font-mono text-sm">
                            {shareableEmail}
                          </span>
                        </li>
                        <li>Confirm the verification email (you'll get a confirmation link)</li>
                        <li>
                          Under <span className="text-white">Filters and Blocked Addresses</span>,
                          create a new filter for your favorite newsletters (e.g., "From:
                          news@substack.com" or "From: morningbrew.com")
                        </li>
                        <li>
                          Choose{" "}
                          <span className="text-white">"Forward it to {shareableEmail}"</span>
                        </li>
                        <li>Save the filter — done!</li>
                      </ol>
                    </div>
                  </section>

                  <section>
                    <h3 className="mb-3 text-xl font-bold">
                      📨 Step 2: Forward newsletters manually (optional)
                    </h3>
                    <p className="mb-4 text-gray-300">
                      You can also send newsletters one by one: Just hit{" "}
                      <span className="text-white">Forward</span> → send to{" "}
                      <span className="rounded bg-gray-800 px-2 py-1 font-mono text-sm">
                        {shareableEmail}
                      </span>
                      .
                    </p>

                    <div className="rounded-lg bg-gray-900 p-6">
                      <h4 className="mb-3 text-xl font-bold">If you'd like to automate it:</h4>
                      <ol className="list-decimal space-y-3 pl-6 text-gray-300">
                        <li>In Gmail, open the newsletter</li>
                        <li>
                          Click the <span className="text-white">three dots</span> →{" "}
                          <span className="text-white">Filter messages like these</span>
                        </li>
                        <li>
                          Choose{" "}
                          <span className="text-white">"Forward it to {shareableEmail}"</span>
                        </li>
                        <li>Save</li>
                      </ol>
                    </div>
                  </section>

                  <section>
                    <h3 className="mb-3 text-xl font-bold">
                      👥 Step 3: Let friends and collaborators contribute
                    </h3>
                    <p className="mb-4 text-gray-300">
                      Others can help build your Lettermeme too! They can forward their own
                      newsletters, essays, or long-form reads — as long as their email addresses are
                      approved under your Lettermeme's "Allowed Senders" settings.
                    </p>
                  </section>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        <section>
          <h2 className="mb-3 text-2xl font-bold">✅ That's it!</h2>
          <p className="mb-4 text-gray-300">
            Once your first newsletters arrive, your Lettermeme will:
          </p>
          <ul className="list-disc space-y-2 pl-6 text-gray-300">
            <li>Summarize them by topic</li>
            <li>Send digests based on your delivery schedule</li>
            <li>Keep growing automatically as new material arrives</li>
          </ul>
        </section>

        <div className="flex gap-4 pt-6">
          <Link to={settingsPath({})}>
            <Button>Go to Profile</Button>
          </Link>
          <Link
            to={lettermemePath({ urlPath: lettermeme.urlPath })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline">View Your Lettermeme</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
